<template>
  <div class="p-8">
    <h2 class="font-semibold uppercase text-gray-600">Push Notification</h2>
    <form @submit.prevent="send" class="flex mt-4 pt-4 border-t">
      <div class="w-1/3">
        <h3 class="text-xl">Send a Push Notification</h3>
        <div class="text-gray-600">Quickly send out a push notification to your users!</div>
      </div>
      <div class="w-2/3">
        <label class="block">
          <span class="text-gray-700">To</span>
          <select @change="setAudience" class="form-select block w-full mt-1">
            <option value="driver">All Drivers</option>
            <option value="passenger">All Passengers</option>
            <option value="all">All</option>
          </select>
        </label>
        <label class="block mt-4">
          <span class="text-gray-700">Title</span>
          <input v-model="title" class="form-input mt-1 block w-full" placeholder="Fare change" />
        </label>
        <label class="block mt-4">
          <span class="text-gray-700">Message</span>
          <textarea
            v-model="body"
            class="form-textarea mt-1 block w-full"
            rows="3"
            placeholder="Push notification message..."
          ></textarea>
        </label>
        <label class="block mt-4">
          <span class="text-gray-700">Message</span>
          <select @change="setChannel" class="form-select block w-full mt-1">
            <option value="push">Push Notification</option>
            <option value="sms">SMS</option>
            <option value="both">Both</option>
          </select>
        </label>
        <div class="block mt-4">
          <button
            :disabled="busy"
            class="px-20 py-2 text-ilift-900 font-semibold rounded"
            :class="
              busy
                ? 'bg-ilift-300 cursor-not-allowed'
                : 'bg-ilift-500 hover:bg-ilift-600'
            "
          >Send</button>
        </div>
      </div>
    </form>
    <hr class="my-6" />

    <div class="-mx-4 sm:-mx-8 px-4 sm:px-8 py-4 overflow-x-auto">
      <div class="flex justify-between">
        <div class="relative max-w-sm w-full">
          <div class="absolute inset-y-0 flex items-center px-3">
            <i class="material-icons text-gray-500">search</i>
          </div>
          <input
            class="rounded border pl-10 pr-4 py-2 w-full"
            v-model="searchKey"
            placeholder="Search"
            @keyup="search"
          />
        </div>

        <div class="w-48">
          <select class="form-select w-full" v-model="type" @change="getNotifications">
            <option :selected="type === 'all'" value="all">All</option>
            <option :selected="type === 'passenger'" value="passenger">Passenger</option>
            <option :selected="type === 'driver'" value="driver">Driver</option>
          </select>
        </div>
      </div>
      <div class="inline-block min-w-full shadow rounded-lg overflow-hidden">
        <table class="min-w-full leading-normal">
          <thead>
            <tr>
              <th
                class="px-5 py-3 border-b-2 border-gray-200 bg-gray-100 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider"
              >No #</th>
              <th
                class="px-5 py-3 border-b-2 border-gray-200 bg-gray-100 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider"
              >Sent To</th>
              <th
                class="px-5 py-3 border-b-2 border-gray-200 bg-gray-100 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider"
              >Message</th>
              <th
                class="px-5 py-3 border-b-2 border-gray-200 bg-gray-100 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider"
              >Sent On</th>
              <th class="px-5 py-3 border-b-2 border-gray-200 bg-gray-100"></th>
            </tr>
          </thead>
          <tbody v-if="notifications.length">
            <tr v-for="notification in notifications" :key="notification._id">
              <td class="px-5 py-5 border-b border-gray-200 bg-white text-sm">
                <p class="text-gray-600 whitespace-no-wrap">{{ notification._id }}</p>
              </td>
              <td class="px-5 py-5 border-b border-gray-200 bg-white text-sm">
                <p class="text-gray-900 whitespace-no-wrap">{{ toString(notification.to) }}</p>
                <!-- <p class="text-gray-600 whitespace-no-wrap">967</p> -->
              </td>
              <td class="px-5 py-5 border-b border-gray-200 bg-white text-sm">
                <p class="text-gray-900 whitespace-no-wrap font-semibold">{{ notification.title }}</p>
                <p class="text-gray-600 whitespace-no-wrap">{{ notification.body }}</p>
              </td>
              <td class="px-5 py-5 border-b border-gray-200 bg-white text-sm">
                <p>{{ fromNow(notification.createdAt) }}</p>
                <p class="text-gray-600 whitespace-no-wrap">{{ formatDate(notification.createdAt) }}</p>
              </td>
              <td class="px-5 py-5 border-b border-gray-200 bg-white text-sm text-right">
                <button type="button" class="inline-block text-gray-500 hover:text-gray-700">
                  <svg class="inline-block h-6 w-6 fill-current" viewBox="0 0 24 24">
                    <path
                      d="M12 6a2 2 0 110-4 2 2 0 010 4zm0 8a2 2 0 110-4 2 2 0 010 4zm-2 6a2 2 0 104 0 2 2 0 00-4 0z"
                    />
                  </svg>
                </button>
              </td>
            </tr>
            <div
              v-if="loading"
              class="absolute inset-0 h-full w-full flex items-center justify-center bg-black bg-opacity-25"
            >
              <i class="material-icons text-6xl spin">sync</i>
              <span class="block invisible">Loading...</span>
            </div>
          </tbody>
          <tbody v-else class="bg-white">
            <tr>
              <td colspan="5">
                <p class="text-center py-8 uppercase">No notification!</p>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <div class="flex justify-between items-end">
        <p class="text-gray-600">
          Showing
          <strong>{{ start }}</strong> to
          <strong>{{ end }}</strong> of
          <strong>{{ paginator.count }}</strong> entries
        </p>
        <div class="flex items-center space-x-4">
          <div>
            Per page:
            <select class="form-select" name="perPage" id="perPage" v-model="perPage" @change="getNotifications">
              <option :selected="perPage === '10'" value="10">10</option>
              <option :selected="perPage === '25'" value="25">25</option>
              <option :selected="perPage === '50'" value="50">50</option>
              <option :selected="perPage === '100'" value="100">100</option>
            </select>
          </div>
          <div class="space-x-2 flex">
            <button
              @click="goToPrev"
              type="button"
              class="flex items-center p-2 text-gray-500 hover:text-gray-800 outline-none focus:outline-none"
            >
              <i class="material-icons">chevron_left</i>
            </button>
            <button
              v-for="page in total"
              :key="`pageLink-${page}`"
              type="button"
              class="p-2"
              :class="{
                'text-gray-500 hover:text-gray-800': currentPage !== page,
                'text-gray-900': currentPage === page,
              }"
              :disabled="currentPage === page"
              @click="goToPage(page)"
            >{{ page }}</button>
            <button
              @click="goToNext"
              type="button"
              class="flex items-center p-2 text-gray-500 hover:text-gray-800 outline-none focus:outline-none"
            >
              <i class="material-icons">chevron_right</i>
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import _ from "lodash";
import moment from "moment";
import Pagination from "@/mixins/Pagination";

export default {
  mixins: [Pagination],
  data() {
    return {
      busy: false,
      to: "driver",
      title: "",
      type: "all",
      searchKey: "",
      loading: true,
      body: "",
      notifications: [],
      change: ""
    };
  },
  created() {
    this.getNotifications();
  },
  watch: {
    currentPage() {
      this.getNotifications();
    },
    perPage() {
      this.getNotifications();
    }
  },
  methods: {
    getNotifications() {
      this.loading = true;
      var path = `/notifications?limit=${this.perPage}&page=${this.currentPage}`;

      if (this.searchKey) {
        path += "&q=" + this.searchKey;
      }

      if (this.type) {
        path += "&to=" + this.type;
      }

      this.$http
        .get(path)
        .then(response => {
          this.notifications = response.data.data;
          this.paginator = {
            nextPage: response.data.nextPage,
            prevPage: response.data.prevPage,
            count: response.data.count
          };
          this.loading = false;
        })
        .catch(err => console.log(err));
    },
    setAudience(e) {
      if (["driver", "passenger", "all"].includes(e.target.value)) {
        this.to = e.target.value;
      }
    },
    setChannel() {},
    search: _.debounce(function(e) {
      this.searchKey = e.target.value.trim();
      this.getNotifications();
    }, 500),
    send() {
      this.busy = true;
      if (!this.to) return;

      this.$http
        .post(`/notifications/topic/${this.to}`, {
          title: this.title,
          body: this.body
        })
        .then(response => {
          console.log(response);
          window.flash({
            type: "success",
            body: "Notification sent successfully!"
          });
          this.title = "";
          this.body = "";
          this.getNotifications();
        })
        .catch(err => console.log(err))
        .finally(() => (this.busy = false));
    },
    formatDate(date) {
      return moment(date).format("MMM D, Y hh:mm A");
    },
    fromNow(date) {
      return moment(date).fromNow();
    },
    toString(to) {
      if (to === "driver") {
        return "All Drivers";
      }
      if (to === "passenger") {
        return "All Passengers";
      }
      if (to === "all") {
        return "All Users";
      }
    }
  }
};
</script>
